import React, { useState } from 'react';
import {
  Container,
  Typography,
  Switch,
  TextField,
  Button,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import ArticleIcon from '@mui/icons-material/Article';

import {
  useProjects,
  useArticles,
  useSocialChannels,
  useLifeMetrics,
  useSiteControls,
  useAboutData,
  useVisitorCounter,
} from '../hooks/useContent';
import { Project, ProjectStatus, LifeMetric } from '../types/content';
import {
  sendAdminUpdate,
  saveProjectsOverride,
  saveLifeMetricsOverride,
  resetAllContentOverrides,
} from '../services/contentApi';
import { ADMIN_API_URL } from '../config/site';
import { AdminLockGate } from '../components/admin/AdminLockGate';

const Admin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('shvms_admin_session_auth') === 'unlocked';
    } catch {
      return false;
    }
  });

  const { allProjects, toggleProjectVisibility } = useProjects();
  const { allArticles, toggleArticleVisibility } = useArticles();
  const { allChannels, toggleSocialVisibility } = useSocialChannels();
  const { allMetrics, toggleMetricVisibility } = useLifeMetrics();
  const { controls, updateControls } = useSiteControls();
  const { about } = useAboutData();
  const visitorCount = useVisitorCounter();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [visibilitySubSection, setVisibilitySubSection] = useState<
    'all' | 'projects' | 'social' | 'blogs' | 'telemetry' | 'schema'
  >('all');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Schema export dialog state
  const [exportDomain, setExportDomain] = useState<
    'projects' | 'social' | 'blogs' | 'telemetry' | 'about' | null
  >(null);
  const [copiedNotification, setCopiedNotification] = useState<boolean>(false);

  // Reset confirmation dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState<boolean>(false);

  // New Project Form State
  const [projectTitle, setProjectTitle] = useState('');
  const [projectTagline, setProjectTagline] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [projectStatus, setProjectStatus] = useState<ProjectStatus>('active');
  const [projectTags, setProjectTags] = useState('');
  const [projectGithub, setProjectGithub] = useState('');
  const [projectLive, setProjectLive] = useState('');

  // Health Metrics Form State
  const [monthKey, setMonthKey] = useState('2026-10');
  const [stepsTotal, setStepsTotal] = useState(280000);
  const [bikeHours, setBikeHours] = useState(24);
  const [gymHours, setGymHours] = useState(20);
  const [sleepAvg, setSleepAvg] = useState(7.2);

  const showNotification = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3500);
  };

  const handleAddProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectTitle.trim()) return;

    const slug = projectTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const tagsArray = projectTags.split(',').map((t) => t.trim()).filter(Boolean);

    const newProject: Project = {
      id: Date.now(),
      slug,
      title: projectTitle.trim(),
      tagline: projectTagline.trim() || null,
      description: projectDesc.trim() || null,
      status: projectStatus,
      thumbnail_url: null,
      tags: tagsArray,
      links: {
        ...(projectGithub ? { github: projectGithub.trim() } : {}),
        ...(projectLive ? { live: projectLive.trim() } : {}),
        actions: projectLive
          ? [{ label: 'Live Link', url: projectLive.trim(), type: 'live' }]
          : [],
      },
      pinned: false,
      visible: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Save locally
    saveProjectsOverride([newProject, ...allProjects]);

    // Send to remote admin proxy if configured
    await sendAdminUpdate('projects', 'POST', newProject);

    showNotification(`Project "${projectTitle}" registered and published!`);
    setProjectTitle('');
    setProjectTagline('');
    setProjectDesc('');
    setProjectTags('');
    setProjectGithub('');
    setProjectLive('');
  };

  const handleSaveHealthMetric = async (e: React.FormEvent) => {
    e.preventDefault();

    const newBatch: LifeMetric[] = [
      { id: Date.now(), month: monthKey, metric: 'steps', value: Number(stepsTotal), unit: 'steps', visible: true },
      { id: Date.now() + 1, month: monthKey, metric: 'bike_hours', value: Number(bikeHours), unit: 'hrs', visible: true },
      { id: Date.now() + 2, month: monthKey, metric: 'gym_hours', value: Number(gymHours), unit: 'hrs', visible: true },
      { id: Date.now() + 3, month: monthKey, metric: 'sleep_avg_hours', value: Number(sleepAvg), unit: 'hrs/night', visible: true },
    ];

    // Filter out previous metrics for the same month and append new batch
    const updated = [...newBatch, ...allMetrics.filter((m) => m.month !== monthKey)];
    saveLifeMetricsOverride(updated);

    await sendAdminUpdate('life_metrics', 'POST', newBatch);
    showNotification(`Monthly health records for ${monthKey} published!`);
  };

  const handleResetOverrides = () => {
    resetAllContentOverrides();
    setResetDialogOpen(false);
    showNotification('All local overrides cleared! Restored original data files.');
  };

  const getDomainData = (domain: string) => {
    switch (domain) {
      case 'projects':
        return allProjects;
      case 'social':
        return allChannels;
      case 'blogs':
        return allArticles;
      case 'telemetry':
        return allMetrics;
      case 'about':
        return about;
      default:
        return {};
    }
  };

  const handleDownloadJson = (domain: string) => {
    const data = getDomainData(domain);
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${domain}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showNotification(`Downloaded ${domain}.json`);
  };

  const handleCopyJson = (domain: string) => {
    const data = getDomainData(domain);
    const jsonStr = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(jsonStr);
    setCopiedNotification(true);
    setTimeout(() => setCopiedNotification(false), 2000);
  };

  if (!isAuthenticated) {
    return (
      <main
        style={{
          position: 'relative',
          zIndex: 1,
          minHeight: 'calc(100vh - 64px)',
          paddingTop: 48,
          paddingBottom: 80,
        }}
      >
        <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
          <AdminLockGate onUnlock={() => setIsAuthenticated(true)} />
        </Container>
      </main>
    );
  }

  const visibleProjectsCount = allProjects.filter((p) => p.visible).length;
  const visibleChannelsCount = allChannels.filter((c) => c.visible).length;
  const visibleBlogsCount = allArticles.filter((b) => b.published).length;
  const visibleMetricsCount = allMetrics.filter((m) => m.visible).length;

  return (
    <main
      style={{
        position: 'relative',
        zIndex: 1,
        minHeight: 'calc(100vh - 64px)',
        paddingTop: 48,
        paddingBottom: 80,
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.4rem' }}>📊</span>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.6rem', sm: '2.2rem' },
                  color: '#FFFFFF',
                }}
              >
                Dashboard Controls & Visibility
              </Typography>
            </div>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
              Choose what to show and what not across projects, social links, blogs, and telemetry.
            </Typography>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.75rem',
                color: ADMIN_API_URL ? '#86EFAC' : '#FCD34D',
                backgroundColor: ADMIN_API_URL ? 'rgba(34, 197, 94, 0.12)' : 'rgba(251, 191, 36, 0.12)',
                border: `1px solid ${ADMIN_API_URL ? 'rgba(34, 197, 94, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
                padding: '5px 12px',
                borderRadius: 8,
                fontWeight: 600,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 14 }} />
              <span>{ADMIN_API_URL ? 'Auth Proxy Connected' : 'Client Mode'}</span>
            </div>

            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                try {
                  sessionStorage.removeItem('shvms_admin_session_auth');
                  sessionStorage.removeItem('shvms_admin_session_timestamp');
                } catch {}
                setIsAuthenticated(false);
              }}
              sx={{
                borderColor: 'rgba(239, 68, 68, 0.35)',
                color: '#FCA5A5',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: 2,
                py: 0.5,
                px: 1.5,
                '&:hover': {
                  borderColor: '#EF4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              Lock Session
            </Button>
          </div>
        </div>

        {/* Success Alert */}
        {successMessage && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              padding: '12px 18px',
              borderRadius: 14,
              color: '#86EFAC',
              fontSize: '0.88rem',
              marginBottom: 24,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 20 }} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Quick Stats Summary Row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 12,
            marginBottom: 28,
          }}
        >
          <div
            style={{
              padding: '14px 16px',
              borderRadius: 14,
              backgroundColor: 'rgba(20, 20, 22, 0.65)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <div style={{ color: '#9CA3AF', fontSize: '0.72rem' }}>Projects Visible</div>
            <div style={{ color: '#38BDF8', fontSize: '1.3rem', fontWeight: 800, marginTop: 4 }}>
              {visibleProjectsCount} <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>/ {allProjects.length}</span>
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              borderRadius: 14,
              backgroundColor: 'rgba(20, 20, 22, 0.65)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <div style={{ color: '#9CA3AF', fontSize: '0.72rem' }}>Social Channels</div>
            <div style={{ color: '#E1306C', fontSize: '1.3rem', fontWeight: 800, marginTop: 4 }}>
              {visibleChannelsCount} <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>/ {allChannels.length}</span>
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              borderRadius: 14,
              backgroundColor: 'rgba(20, 20, 22, 0.65)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <div style={{ color: '#9CA3AF', fontSize: '0.72rem' }}>Blogs Published</div>
            <div style={{ color: '#FACC15', fontSize: '1.3rem', fontWeight: 800, marginTop: 4 }}>
              {visibleBlogsCount} <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>/ {allArticles.length}</span>
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              borderRadius: 14,
              backgroundColor: 'rgba(20, 20, 22, 0.65)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <div style={{ color: '#9CA3AF', fontSize: '0.72rem' }}>Telemetry Metrics</div>
            <div style={{ color: '#4ADE80', fontSize: '1.3rem', fontWeight: 800, marginTop: 4 }}>
              {visibleMetricsCount} <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>/ {allMetrics.length}</span>
            </div>
          </div>

          <div
            style={{
              padding: '14px 16px',
              borderRadius: 14,
              backgroundColor: 'rgba(20, 20, 22, 0.65)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <div style={{ color: '#9CA3AF', fontSize: '0.72rem' }}>Unique Visitors</div>
            <div style={{ color: '#EFC430', fontSize: '1.3rem', fontWeight: 800, marginTop: 4 }}>
              {visitorCount !== null ? visitorCount.toLocaleString() : 'N/A'}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            backgroundColor: 'rgba(20, 20, 22, 0.65)',
            backdropFilter: 'blur(16px)',
            borderRadius: '16px 16px 0 0',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderBottom: 'none',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            textColor="inherit"
            sx={{
              '& .MuiTabs-indicator': { backgroundColor: '#EFC430' },
              '& .MuiTab-root': {
                color: '#9CA3AF',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.88rem',
                py: 2,
                '&.Mui-selected': { color: '#EFC430' },
              },
            }}
          >
            <Tab icon={<TuneIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Choose What to Show" />
            <Tab icon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Append Project" />
            <Tab icon={<DirectionsBikeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Upload Telemetry" />
          </Tabs>
        </div>

        {/* Tab Content Body */}
        <div
          style={{
            backgroundColor: 'rgba(20, 20, 22, 0.65)',
            backdropFilter: 'blur(16px)',
            borderRadius: '0 0 16px 16px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            padding: '28px',
          }}
        >
          {/* TAB 0: Choose What to Show & Visibility Management */}
          {activeTab === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Sub-navigation filter chips */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {(
                  [
                    { key: 'all', label: 'All Items' },
                    { key: 'projects', label: `Projects (${visibleProjectsCount}/${allProjects.length})` },
                    { key: 'social', label: `Social Links (${visibleChannelsCount}/${allChannels.length})` },
                    { key: 'blogs', label: `Blogs (${visibleBlogsCount}/${allArticles.length})` },
                    { key: 'telemetry', label: `Telemetry (${visibleMetricsCount}/${allMetrics.length})` },
                    { key: 'schema', label: 'Schema Files & Export' },
                  ] as const
                ).map((sub) => (
                  <Button
                    key={sub.key}
                    size="small"
                    onClick={() => setVisibilitySubSection(sub.key)}
                    sx={{
                      backgroundColor:
                        visibilitySubSection === sub.key ? '#EFC430' : 'rgba(255, 255, 255, 0.04)',
                      color: visibilitySubSection === sub.key ? '#0A0A0A' : '#D1D5DB',
                      fontWeight: visibilitySubSection === sub.key ? 700 : 500,
                      fontSize: '0.78rem',
                      textTransform: 'none',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.5,
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      '&:hover': {
                        backgroundColor:
                          visibilitySubSection === sub.key ? '#EFC430' : 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {sub.label}
                  </Button>
                ))}
              </div>

              {/* 1. Global Site Controls (Shown on 'all') */}
              {(visibilitySubSection === 'all') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Typography variant="subtitle2" sx={{ color: '#EFC430', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    🌐 Global Header & Dashboard Switches
                  </Typography>

                  {/* Location Beacon */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 18px',
                      borderRadius: 14,
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <div>
                      <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.9rem' }}>
                        Location Beacon
                      </div>
                      <div style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
                        Coarse location pill displayed on header.
                      </div>
                    </div>
                    <Switch
                      checked={controls.show_location}
                      onChange={() => updateControls({ show_location: !controls.show_location })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#EFC430',
                          '& + .MuiSwitch-track': { backgroundColor: '#EFC430' },
                        },
                      }}
                    />
                  </div>

                  {controls.show_location && (
                    <TextField
                      label="Location Label (Coarse)"
                      size="small"
                      value={controls.location_label}
                      onChange={(e) => updateControls({ location_label: e.target.value })}
                      placeholder="e.g. India"
                      InputLabelProps={{ style: { color: '#9CA3AF' } }}
                      InputProps={{ style: { color: '#FFFFFF' } }}
                    />
                  )}

                  {/* Life Pulse Widget */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 18px',
                      borderRadius: 14,
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <div>
                      <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.9rem' }}>
                        Life Pulse Telemetry Widget
                      </div>
                      <div style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
                        Monthly health, saddle time & sleep telemetry tile on Home dashboard.
                      </div>
                    </div>
                    <Switch
                      checked={controls.show_life_pulse}
                      onChange={() => updateControls({ show_life_pulse: !controls.show_life_pulse })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#EFC430',
                          '& + .MuiSwitch-track': { backgroundColor: '#EFC430' },
                        },
                      }}
                    />
                  </div>

                  {/* Visitor Counter */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 18px',
                      borderRadius: 14,
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <div>
                      <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.9rem' }}>
                        Visitor Counter Badge
                      </div>
                      <div style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
                        Displays unique visitor visits count in header.
                      </div>
                    </div>
                    <Switch
                      checked={controls.show_visitor_count}
                      onChange={() => updateControls({ show_visitor_count: !controls.show_visitor_count })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#EFC430',
                          '& + .MuiSwitch-track': { backgroundColor: '#EFC430' },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              {/* 2. Projects Visibility Section */}
              {(visibilitySubSection === 'all' || visibilitySubSection === 'projects') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ color: '#38BDF8', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      🔨 Projects Visibility ({visibleProjectsCount}/{allProjects.length} Visible)
                    </Typography>
                    <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>projects.json</span>
                  </div>

                  {allProjects.map((proj) => (
                    <div
                      key={proj.slug}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        borderRadius: 14,
                        backgroundColor: proj.visible ? 'rgba(56, 189, 248, 0.03)' : 'rgba(255, 255, 255, 0.01)',
                        border: proj.visible
                          ? '1px solid rgba(56, 189, 248, 0.2)'
                          : '1px solid rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div style={{ flex: 1, paddingRight: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.95rem' }}>
                            {proj.title}
                          </span>
                          <Chip
                            label={proj.status}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              backgroundColor: 'rgba(255, 255, 255, 0.06)',
                              color: '#D1D5DB',
                            }}
                          />
                          {proj.pinned && (
                            <Chip
                              label="Pinned"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                backgroundColor: 'rgba(239, 196, 48, 0.15)',
                                color: '#EFC430',
                              }}
                            />
                          )}
                        </div>

                        {proj.tagline && (
                          <div style={{ color: '#9CA3AF', fontSize: '0.8rem', lineHeight: 1.4 }}>
                            {proj.tagline}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                          {proj.tags.slice(0, 4).map((t) => (
                            <span
                              key={t}
                              style={{
                                fontSize: '0.68rem',
                                color: '#9CA3AF',
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                padding: '1px 6px',
                                borderRadius: 4,
                              }}
                            >
                              {t}
                            </span>
                          ))}
                          {proj.links.actions && proj.links.actions.length > 0 && (
                            <span style={{ fontSize: '0.68rem', color: '#38BDF8', fontWeight: 600 }}>
                              • {proj.links.actions.length} action link(s)
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: proj.visible ? '#4ADE80' : '#6B7280',
                            backgroundColor: proj.visible ? 'rgba(74, 222, 128, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                            padding: '3px 8px',
                            borderRadius: 6,
                          }}
                        >
                          {proj.visible ? 'VISIBLE' : 'HIDDEN'}
                        </span>
                        <Switch
                          checked={proj.visible}
                          onChange={() => {
                            toggleProjectVisibility(proj.slug);
                            showNotification(
                              `Project "${proj.title}" ${!proj.visible ? 'is now visible' : 'hidden'}`
                            );
                          }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#38BDF8',
                              '& + .MuiSwitch-track': { backgroundColor: '#38BDF8' },
                            },
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 3. Social Connect Channels Visibility Section */}
              {(visibilitySubSection === 'all' || visibilitySubSection === 'social') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ color: '#E1306C', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      🔗 Social Connect Channels ({visibleChannelsCount}/{allChannels.length} Visible)
                    </Typography>
                    <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>social.json</span>
                  </div>

                  {allChannels.map((channel) => (
                    <div
                      key={channel.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        borderRadius: 14,
                        backgroundColor: channel.visible ? 'rgba(225, 48, 108, 0.03)' : 'rgba(255, 255, 255, 0.01)',
                        border: channel.visible
                          ? '1px solid rgba(225, 48, 108, 0.2)'
                          : '1px solid rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
                        <div
                          style={{
                            padding: 8,
                            borderRadius: 10,
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {channel.icon === 'instagram' && <InstagramIcon sx={{ fontSize: 20, color: channel.accent }} />}
                          {channel.icon === 'github' && <GitHubIcon sx={{ fontSize: 20, color: channel.accent }} />}
                          {channel.icon === 'linkedin' && <LinkedInIcon sx={{ fontSize: 20, color: channel.accent }} />}
                          {channel.icon === 'twitter' && <TwitterIcon sx={{ fontSize: 20, color: channel.accent }} />}
                          {channel.icon === 'email' && <EmailIcon sx={{ fontSize: 20, color: channel.accent }} />}
                        </div>

                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.92rem' }}>
                              {channel.platform}
                            </span>
                            <span style={{ color: channel.accent, fontSize: '0.78rem', fontWeight: 600 }}>
                              {channel.handle}
                            </span>
                          </div>
                          <div style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
                            {channel.context} — {channel.description}
                          </div>
                          <a
                            href={channel.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#6B7280', fontSize: '0.72rem', textDecoration: 'none' }}
                          >
                            {channel.url} ↗
                          </a>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: channel.visible ? '#4ADE80' : '#6B7280',
                            backgroundColor: channel.visible ? 'rgba(74, 222, 128, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                            padding: '3px 8px',
                            borderRadius: 6,
                          }}
                        >
                          {channel.visible ? 'VISIBLE' : 'HIDDEN'}
                        </span>
                        <Switch
                          checked={channel.visible}
                          onChange={() => {
                            toggleSocialVisibility(channel.id);
                            showNotification(
                              `Channel "${channel.platform}" ${!channel.visible ? 'is now visible' : 'hidden'}`
                            );
                          }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: channel.accent,
                              '& + .MuiSwitch-track': { backgroundColor: channel.accent },
                            },
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 4. Blogs & Notes Visibility Section */}
              {(visibilitySubSection === 'all' || visibilitySubSection === 'blogs') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ color: '#FACC15', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      📝 Blogs & Notes Visibility ({visibleBlogsCount}/{allArticles.length} Published)
                    </Typography>
                    <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>blogs.json</span>
                  </div>

                  {allArticles.map((art) => (
                    <div
                      key={art.slug}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        borderRadius: 14,
                        backgroundColor: art.published ? 'rgba(250, 204, 21, 0.03)' : 'rgba(255, 255, 255, 0.01)',
                        border: art.published
                          ? '1px solid rgba(250, 204, 21, 0.2)'
                          : '1px solid rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div style={{ flex: 1, paddingRight: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <ArticleIcon sx={{ fontSize: 16, color: '#EFC430' }} />
                          <span style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.92rem' }}>
                            {art.title}
                          </span>
                          <span
                            style={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              color: '#EFC430',
                              backgroundColor: 'rgba(239, 196, 48, 0.1)',
                              padding: '2px 6px',
                              borderRadius: 4,
                            }}
                          >
                            {art.format}
                          </span>
                        </div>

                        {art.excerpt && (
                          <div style={{ color: '#9CA3AF', fontSize: '0.78rem', lineHeight: 1.4 }}>
                            {art.excerpt}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                          {art.tags.map((t) => (
                            <span
                              key={t}
                              style={{
                                fontSize: '0.68rem',
                                color: '#9CA3AF',
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                padding: '1px 6px',
                                borderRadius: 4,
                              }}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: art.published ? '#4ADE80' : '#6B7280',
                            backgroundColor: art.published ? 'rgba(74, 222, 128, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                            padding: '3px 8px',
                            borderRadius: 6,
                          }}
                        >
                          {art.published ? 'PUBLISHED' : 'DRAFT'}
                        </span>
                        <Switch
                          checked={art.published}
                          onChange={() => {
                            toggleArticleVisibility(art.slug);
                            showNotification(
                              `Article "${art.title}" ${!art.published ? 'is now published' : 'hidden'}`
                            );
                          }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#FACC15',
                              '& + .MuiSwitch-track': { backgroundColor: '#FACC15' },
                            },
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 5. Telemetry Metrics Visibility Section */}
              {(visibilitySubSection === 'all' || visibilitySubSection === 'telemetry') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" sx={{ color: '#4ADE80', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      ⚡ Telemetry Metrics ({visibleMetricsCount}/{allMetrics.length} Visible)
                    </Typography>
                    <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>telemetry.json</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                    {allMetrics.map((met) => {
                      const identifier = met.id || `${met.month}-${met.metric}`;
                      return (
                        <div
                          key={identifier}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '14px 16px',
                            borderRadius: 14,
                            backgroundColor: met.visible ? 'rgba(74, 222, 128, 0.03)' : 'rgba(255, 255, 255, 0.01)',
                            border: met.visible
                              ? '1px solid rgba(74, 222, 128, 0.2)'
                              : '1px solid rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          <div>
                            <div style={{ color: '#EFC430', fontSize: '0.72rem', fontWeight: 700 }}>
                              {met.month}
                            </div>
                            <div style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.88rem' }}>
                              {met.metric.replace('_', ' ').toUpperCase()}
                            </div>
                            <div style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>
                              {met.value.toLocaleString()} {met.unit}
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Switch
                              size="small"
                              checked={met.visible}
                              onChange={() => {
                                toggleMetricVisibility(identifier);
                                showNotification(`Metric ${met.metric} for ${met.month} toggled!`);
                              }}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4ADE80',
                                  '& + .MuiSwitch-track': { backgroundColor: '#4ADE80' },
                                },
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 6. Schema Files & Export Section */}
              {(visibilitySubSection === 'all' || visibilitySubSection === 'schema') && (
                <div
                  style={{
                    padding: '20px',
                    borderRadius: 16,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    marginTop: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                      <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                        💾 Data Schema Management & JSON Export
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        All site data is organized into structured JSON files with fixed schemas. Download or view current data:
                      </Typography>
                    </div>

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RestartAltIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setResetDialogOpen(true)}
                      sx={{
                        color: '#F87171',
                        borderColor: 'rgba(239, 68, 68, 0.3)',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.08)' },
                      }}
                    >
                      Reset Overrides
                    </Button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 14 }}>
                    {(
                      [
                        { id: 'projects', label: 'projects.json', icon: '🔨' },
                        { id: 'social', label: 'social.json', icon: '🔗' },
                        { id: 'blogs', label: 'blogs.json', icon: '📝' },
                        { id: 'telemetry', label: 'telemetry.json', icon: '⚡' },
                        { id: 'about', label: 'about.json', icon: '👤' },
                      ] as const
                    ).map((file) => (
                      <div
                        key={file.id}
                        style={{
                          padding: '12px',
                          borderRadius: 12,
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#FFFFFF', fontWeight: 600, fontSize: '0.8rem' }}>
                          <span>{file.icon}</span>
                          <span>{file.label}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setExportDomain(file.id)}
                            sx={{
                              fontSize: '0.68rem',
                              textTransform: 'none',
                              color: '#D1D5DB',
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                              p: '2px 8px',
                              minWidth: 'auto',
                              flex: 1,
                            }}
                          >
                            View
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadJson(file.id)}
                            sx={{ color: '#EFC430', p: '2px 4px' }}
                            title={`Download ${file.label}`}
                          >
                            <FileDownloadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 1: Append New Project */}
          {activeTab === 1 && (
            <form onSubmit={handleAddProject} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.1rem' }}>
                Append New Vibe Project
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF', mt: -1 }}>
                New projects are immediately stored with full schema and can be toggled on or off at any time.
              </Typography>

              <TextField
                label="Project Title"
                size="small"
                required
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                placeholder="e.g. Night Rider HUD"
                InputLabelProps={{ style: { color: '#9CA3AF' } }}
                InputProps={{ style: { color: '#FFFFFF' } }}
              />

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <TextField
                  select
                  label="Status"
                  size="small"
                  value={projectStatus}
                  onChange={(e) => setProjectStatus(e.target.value as ProjectStatus)}
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="active">Active Beta</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="idea">In Development / Idea</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </TextField>

                <TextField
                  label="Technology Tags (comma separated)"
                  size="small"
                  value={projectTags}
                  onChange={(e) => setProjectTags(e.target.value)}
                  placeholder="Go, Docker, GPS, React"
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </div>

              <TextField
                label="Tagline / One-liner"
                size="small"
                value={projectTagline}
                onChange={(e) => setProjectTagline(e.target.value)}
                placeholder="A high-level summary of what this project does."
                InputLabelProps={{ style: { color: '#9CA3AF' } }}
                InputProps={{ style: { color: '#FFFFFF' } }}
              />

              <TextField
                label="Description (Markdown)"
                multiline
                rows={4}
                value={projectDesc}
                onChange={(e) => setProjectDesc(e.target.value)}
                placeholder="Write detailed architecture, technical highlights, and invariants..."
                InputLabelProps={{ style: { color: '#9CA3AF' } }}
                InputProps={{ style: { color: '#FFFFFF' } }}
              />

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <TextField
                  label="GitHub Repo URL (Optional)"
                  size="small"
                  value={projectGithub}
                  onChange={(e) => setProjectGithub(e.target.value)}
                  placeholder="https://github.com/devshvms/..."
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                  sx={{ flex: 1, minWidth: 200 }}
                />

                <TextField
                  label="Live App / Landing URL (Optional)"
                  size="small"
                  value={projectLive}
                  onChange={(e) => setProjectLive(e.target.value)}
                  placeholder="https://trackme.shvms.in/"
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </div>

              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: '#EFC430',
                  color: '#0A0A0A',
                  fontWeight: 700,
                  textTransform: 'none',
                  py: 1.2,
                  borderRadius: 2,
                  mt: 1,
                  '&:hover': { backgroundColor: '#F4D675' },
                }}
              >
                Publish Project to Schema
              </Button>
            </form>
          )}

          {/* TAB 2: Upload Health Telemetry Data */}
          {activeTab === 2 && (
            <form onSubmit={handleSaveHealthMetric} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.1rem' }}>
                Publish Monthly Health Batch
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF', mt: -1 }}>
                Publish monthly aggregated steps, gym time, saddle time, and sleep. (Privacy-first 30-day delay).
              </Typography>

              <TextField
                label="Month Key (YYYY-MM)"
                size="small"
                required
                value={monthKey}
                onChange={(e) => setMonthKey(e.target.value)}
                placeholder="2026-10"
                InputLabelProps={{ style: { color: '#9CA3AF' } }}
                InputProps={{ style: { color: '#FFFFFF' } }}
                sx={{ maxWidth: 200 }}
              />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                <TextField
                  label="Steps Total"
                  type="number"
                  size="small"
                  required
                  value={stepsTotal}
                  onChange={(e) => setStepsTotal(Number(e.target.value))}
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                />

                <TextField
                  label="Bike Saddle Hours"
                  type="number"
                  size="small"
                  required
                  value={bikeHours}
                  onChange={(e) => setBikeHours(Number(e.target.value))}
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                />

                <TextField
                  label="Gym Hours"
                  type="number"
                  size="small"
                  required
                  value={gymHours}
                  onChange={(e) => setGymHours(Number(e.target.value))}
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                />

                <TextField
                  label="Sleep Average (Hours/Night)"
                  type="number"
                  inputProps={{ step: '0.1' }}
                  size="small"
                  required
                  value={sleepAvg}
                  onChange={(e) => setSleepAvg(Number(e.target.value))}
                  InputLabelProps={{ style: { color: '#9CA3AF' } }}
                  InputProps={{ style: { color: '#FFFFFF' } }}
                />
              </div>

              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: '#4ADE80',
                  color: '#0A0A0A',
                  fontWeight: 700,
                  textTransform: 'none',
                  py: 1.2,
                  borderRadius: 2,
                  mt: 1,
                  '&:hover': { backgroundColor: '#86EFAC' },
                }}
              >
                Save Health Batch to Telemetry
              </Button>
            </form>
          )}
        </div>

        {/* View/Export JSON Dialog */}
        <Dialog
          open={exportDomain !== null}
          onClose={() => setExportDomain(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: '#141416',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 16,
              color: '#FFFFFF',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Schema: {exportDomain}.json</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={() => exportDomain && handleCopyJson(exportDomain)}
                sx={{ color: '#EFC430', textTransform: 'none', fontSize: '0.75rem' }}
              >
                {copiedNotification ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                size="small"
                startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
                onClick={() => exportDomain && handleDownloadJson(exportDomain)}
                sx={{ color: '#38BDF8', textTransform: 'none', fontSize: '0.75rem' }}
              >
                Download
              </Button>
            </div>
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}>
            <pre
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '16px',
                borderRadius: 10,
                fontSize: '0.8rem',
                color: '#A7F3D0',
                overflowX: 'auto',
                maxHeight: '60vh',
                fontFamily: 'monospace',
                margin: 0,
              }}
            >
              {exportDomain ? JSON.stringify(getDomainData(exportDomain), null, 2) : ''}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setExportDomain(null)}
              sx={{ color: '#9CA3AF', textTransform: 'none' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Confirmation Dialog */}
        <Dialog
          open={resetDialogOpen}
          onClose={() => setResetDialogOpen(false)}
          PaperProps={{
            style: {
              backgroundColor: '#141416',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 16,
              color: '#FFFFFF',
            },
          }}
        >
          <DialogTitle sx={{ color: '#F87171' }}>Reset All Overrides?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#D1D5DB', lineHeight: 1.6 }}>
              This will clear all local visibility toggles and overrides from localStorage and revert all domains back to their original data files (projects.json, social.json, blogs.json, telemetry.json, about.json).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)} sx={{ color: '#9CA3AF', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleResetOverrides}
              variant="contained"
              sx={{ backgroundColor: '#EF4444', color: '#FFFFFF', textTransform: 'none', fontWeight: 600 }}
            >
              Confirm Reset
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </main>
  );
};

export default Admin;
